/**
 * alp-cart.js — shared add-to-cart helper for the Alpine PDP.
 *
 * Single source for the "POST /cart/add.js → refresh mini-cart → open drawer"
 * flow that add-to-cart.liquid, make-it-a-match.liquid and quick-add-modal.liquid
 * each used to copy. Keeping it here means the three stay in sync.
 *
 * Locale-safe: reads window.routes.cart_add_url (set in layout/theme.liquid) so
 * it POSTs to the right localized route; falls back to '/cart/add'.
 *
 * Usage (returns a promise; THROWS on HTTP failure so callers can show an error):
 *   await window.alpAddToCart({ id: variantId, quantity: 1 });
 *   await window.alpAddToCart({ id }, { openDrawer: false }); // add without opening
 *
 * Loaded with `defer` like the other alp-*.js assets. Framework-agnostic — it
 * touches no Alpine state, so it works from any block, store or vanilla handler.
 */
(function () {
  'use strict';
  if (window.alpAddToCart) return;

  // ---- Analytics -----------------------------------------------------------
  // Mirror the legacy `addToCart` dataLayer push (assets/global.js) for EVERY
  // Alpine add path (PDP block, quick-add modal, make-it-a-match, bundles), which
  // otherwise fire no analytics. Built from the /cart/add.js response the callers
  // already receive; same custom event name + payload so existing GTM tags fire.
  // Never throws into the cart flow.
  function pushAddToCartDL(data, location) {
    try {
      if (!data) return;
      window.dataLayer = window.dataLayer || [];
      var money = function (c) { return (Number(c || 0) / 100).toFixed(2); };
      var opts = data.variant_options || [];
      var color = opts.length ? String(opts[0]).trim() : '';
      var size = opts.length > 1 ? String(opts[1]).trim() : '';
      var props = data.properties || {};
      var expectedShipDate = props['EXPECTED SHIP DATE'] || '';
      var expectedToShip = props['EXPECTED TO SHIP'] || '';
      // compare-at isn't in the add.js response — reuse the legacy productVariants
      // lookup when present, else fall back to the line's pre-discount price.
      var sv = (window.productVariants || []).find(function (v) { return Number(v.id) === Number(data.variant_id); });
      var vPrice = Number((sv && sv.price) || data.original_price || data.final_price || 0);
      var vCompare = Number((sv && sv.compare_at_price) || 0);
      var baseCents = vCompare > vPrice ? vCompare : vPrice;

      window.dataLayer.push({
        event: 'addToCart',
        addToCart: {
          products: [{
            id: data.product_id,
            sku: data.variant_id,
            style: data.sku,
            name: data.title,
            price: money(data.final_price),
            basePrice: money(baseCents),
            quantity: data.quantity,
            brand: data.vendor,
            color: color,
            colorFamily: color,
            size: size,
            expectedShipDate: expectedShipDate,
            upc: props.UPC || props['UPC'] || '',
          }],
          IsPreOrder: props['Pre-ordered items'] != null,
          IsBackOrder: props['Back-ordered items'] != null,
          expectedShipDate: expectedShipDate || expectedToShip,
          addToCartLocation: location || window.addToCartLocationOverride || 'Product Page',
        },
        _clear: true,
      });
    } catch (e) {
      console.warn('[alp-cart] addToCart dataLayer push failed', e);
    }
  }

  // Legacy "side cart viewed" push — fired once when the drawer opens.
  function pushViewedSideCartDL() {
    try {
      if (typeof window.getEnrichedCart === 'function') window.getEnrichedCart();
      (window.dataLayer = window.dataLayer || []).push({ event: 'viewedSideCart', _clear: true });
    } catch (e) {
      console.warn('[alp-cart] viewedSideCart dataLayer push failed', e);
    }
  }
  // --------------------------------------------------------------------------

  async function alpAddToCart(item, opts) {
    opts = opts || {};
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    var res = await fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });
    if (!res.ok) throw new Error('cart/add ' + res.status);
    var data = await res.json();

    // One refresh is enough — updateminicart/updatecart do the same
    // header-minicart fetch+replace; calling both doubled the work.
    if (window.updateminicart) await window.updateminicart();
    else if (window.updatecart) await window.updatecart();

    // Analytics: mirror the legacy addToCart dataLayer event for the Alpine path.
    pushAddToCartDL(data, opts.addToCartLocation);

    // Announce the completed add so listeners can react (e.g. the quick-add modal
    // self-closes + attributes the add to Stylitics). Additive: no existing caller
    // listens for this, so it is safe for everyone already using alpAddToCart().
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: item, cart: data } }));

    // Open the cart drawer by clicking the header bubble (same trigger the blocks
    // use). Pass { openDrawer: false } to add silently.
    if (opts.openDrawer !== false) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.click();
      pushViewedSideCartDL(); // side cart is now open
    }

    return data;
  }

  window.alpAddToCart = alpAddToCart;

  /**
   * alpAddBundle — add several lines as one bundle, all-or-nothing.
   *
   * Adds each { id, quantity, properties } via /cart/add.js with 60ms spacing
   * (the same cadence the charm bundle uses in cart-bundle.js), collecting the
   * returned line keys. If ANY add fails, every already-added key is rolled back
   * via cart_update_url (qty 0) and the error is re-thrown — the cart never ends
   * up holding a half-built bundle. On success, refreshes the mini-cart and opens
   * the drawer (same tail as alpAddToCart). Pass { openDrawer: false } to stay closed.
   *
   * Returns the array of added line keys.
   */
  async function alpAddBundle(items, opts) {
    opts = opts || {};
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
    var addedKeys = [];

    try {
      for (var i = 0; i < items.length; i++) {
        await new Promise(function (r) { setTimeout(r, 60); });
        var res = await fetch(addUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(items[i]),
        });
        if (!res.ok) throw new Error('cart/add ' + res.status);
        var data = await res.json();
        if (data && data.key) addedKeys.push(data.key);
        pushAddToCartDL(data, opts.addToCartLocation); // one addToCart per bundle line
      }
    } catch (e) {
      if (addedKeys.length) {
        var updates = {};
        addedKeys.forEach(function (k) { updates[k] = 0; });
        await fetch(updateUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
        }).catch(function () {});
      }
      throw e;
    }

    if (window.updateminicart) await window.updateminicart();
    else if (window.updatecart) await window.updatecart();

    if (opts.openDrawer !== false) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.click();
      pushViewedSideCartDL(); // side cart is now open
    }

    return addedKeys;
  }

  window.alpAddBundle = alpAddBundle;
})();
